import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from './useInView';
import {
  HiOutlineSparkles,
  HiOutlineHeart,
  HiOutlineSun,
  HiOutlineEye,
  HiOutlineBeaker,
  HiOutlineFire,
  HiOutlineColorSwatch,
  HiOutlineShieldCheck,
} from 'react-icons/hi';

const services = [
  {
    icon: HiOutlineSparkles,
    title: 'Advanced Botox Therapy',
    description:
      'Comprehensive botox solutions from fine lines to medical-grade hyperhidrosis treatment, delivered with precision and artistry.',
    features: ['Full Face Botox', 'Swan Neck Botox', 'Palms & Feet Hyperhidrosis', 'Forehead Lines', 'Crow\'s Feet', 'Frown Lines'],
  },
  {
    icon: HiOutlineHeart,
    title: 'Dermal Fillers',
    description:
      'Restore volume, enhance contours, and achieve beautiful symmetry with premium hyaluronic acid fillers.',
    features: ['Lip Enhancement', 'Cheek Sculpting', 'Jawline Definition', 'Full Face Transformation'],
  },
  {
    icon: HiOutlineFire,
    title: 'Fat Removal',
    description:
      'Targeted fat reduction treatments to sculpt and contour your body for a slimmer, more defined silhouette.',
    features: ['Double Chin', 'Love Handles', 'Inner Thighs', 'Abdomen', 'Back'],
  },
  {
    icon: HiOutlineEye,
    title: 'Under-Eye Treatment',
    description:
      'Targeted solutions for dark circles, hollow tear troughs, and tired-looking eyes.',
    features: ['Tear Trough Filler', 'PRP Therapy', 'Eye Rejuvenation'],
  },
  {
    icon: HiOutlineBeaker,
    title: 'Mesotherapy',
    description:
      'Revitalize your skin with targeted microinjections of vitamins, minerals, and amino acids for deep nourishment and glow.',
    features: ['Skin Boosters', 'Hair Restoration', 'Face Rejuvenation', 'Body Contouring'],
  },
  {
    icon: HiOutlineColorSwatch,
    title: 'Exosomes Therapy',
    description:
      'Next-generation regenerative treatment using exosomes to accelerate skin repair, reduce inflammation, and promote collagen production.',
    features: ['Skin Regeneration', 'Anti-Aging', 'Hair Growth', 'Post-Treatment Recovery'],
  },
  {
    icon: HiOutlineShieldCheck,
    title: 'Salmon DNA Therapy',
    description:
      'Innovative polynucleotide therapy derived from salmon DNA to deeply hydrate, repair, and rejuvenate skin at the cellular level.',
    features: ['Deep Hydration', 'Skin Elasticity', 'Fine Line Repair', 'Radiance Boost'],
  },
  {
    icon: HiOutlineSun,
    title: 'Skin Rejuvenation',
    description:
      'Advanced treatments to improve skin texture, tone, and radiance for a youthful, glowing complexion.',
    features: ['Chemical Peels', 'Microneedling', 'LED Therapy'],
  },
];

export default function Services() {
  const [ref, inView] = useInView({ threshold: 0.1 });

  return (
    <section id="services" ref={ref} className="section-padding bg-cream">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center max-w-2xl mx-auto mb-16 lg:mb-20"
        >
          <div className="flex items-center justify-center gap-4 mb-6">
            <span className="gold-line" />
            <span className="text-xs tracking-[0.3em] uppercase text-gold-600 font-medium">
              Our Services
            </span>
            <span className="gold-line" />
          </div>
          <h2 className="heading-lg mb-6">
            Treatments Tailored{' '}
            <span className="italic text-gold-500">to You</span>
          </h2>
          <p className="body-lg">
            From subtle enhancements to transformative procedures, each treatment
            is customized to your unique goals with the highest standard of care.
          </p>
        </motion.div>

        {/* Services grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {services.map((service, i) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              className="group bg-white rounded-2xl p-8 lg:p-10 transition-all duration-500 hover:shadow-xl hover:shadow-gold-100/50 hover:-translate-y-1"
            >
              <div className="w-12 h-12 rounded-xl bg-gold-50 flex items-center justify-center mb-6 group-hover:bg-gold-100 transition-colors duration-300">
                <service.icon className="w-6 h-6 text-gold-500" />
              </div>
              <h3 className="font-serif text-xl font-medium mb-3 text-charcoal-900">
                {service.title}
              </h3>
              <p className="text-sm text-charcoal-500 leading-relaxed mb-6">
                {service.description}
              </p>
              <div className="flex flex-wrap gap-2">
                {service.features.map((feature) => (
                  <span
                    key={feature}
                    className="text-[11px] tracking-wider uppercase px-3 py-1.5 rounded-full bg-cream text-charcoal-500 border border-charcoal-100"
                  >
                    {feature}
                  </span>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="text-center mt-16"
        >
          <a href="#contact" className="btn-primary">
            Book a Consultation
          </a>
        </motion.div>
      </div>
    </section>
  );
}
